import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useTranslation } from '../translations';
import SafeIcon from '../components/SafeIcon';
import QuantumPlanetBackground from '../components/QuantumPlanetBackground';
import * as FiIcons from 'react-icons/fi';
import { HiOfficeBuilding } from 'react-icons/hi';
import { GiSpartanHelmet } from 'react-icons/gi';
import { FaBalanceScale, FaHandshake } from 'react-icons/fa';
import appScreenshot from '../assets/app-screenshot.png';
import demoScreenshot from '../assets/demo-screenshot.png';
import annualZapIcon from '../assets/annual-zap-icon.png';
import pricingLogoClean from '../assets/pricing-logo-clean.png';
import panamaFlag from '../assets/panama-flag.png';
import { PLAN_CONFIGS, calculateYearlyPrice } from '../config/planConfig';

const { FiBarChart2, FiCpu, FiShield, FiUsers, FiTrendingUp, FiTrendingDown, FiZap, FiTarget, FiActivity, FiLayers, FiCheckCircle, FiArrowRight, FiPlay, FiChevronDown, FiCheck, FiSend, FiMail, FiPhone, FiMapPin, FiDatabase, FiHeadphones, FiDollarSign, FiCamera, FiHome, FiFileText, FiClock, FiAlertTriangle, FiLock, FiGlobe } = FiIcons;

// Icon mapping for plans (maps plan id to icon)
const PLAN_ICONS = {
  starter: FiIcons.FiUser,
  pro_trader: FiTrendingUp,
  institutional_ai: HiOfficeBuilding,
  enterprise: FaHandshake,
};

// Pricing Section Component - USES CENTRALIZED PLAN CONFIG
const PricingSection = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="pricing" className="relative py-32 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0d1a2d] border border-cyan-500/30 mb-8 shadow-lg shadow-cyan-500/10">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-500"></span>
            </span>
            <span className="text-sm font-medium bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Flexible Institutional Pricing
            </span>
          </div>
          <h2 className="text-5xl md:text-6xl font-black mb-4">
            <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
              Pricing Plans
            </span>
          </h2>
          <div className="mb-6">
            <span className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Trade With Enterprise-Grade Discipline — Tailored for Your Organization</span>
          </div>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
            We deliver customized Quantum AI FinTech solutions tailored for financial institutions. Protect capital, optimize multi-asset portfolios, and automate decision-making with <span className="text-cyan-400 font-semibold">enterprise-grade AI risk management, quantum-inspired algorithms, and advanced trading technology.</span>
          </p>
        </motion.div>

        {/* CTA Box */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative max-w-4xl mx-auto"
        >
          {/* Background Glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-cyan-600/20 to-pink-600/20 rounded-2xl blur-2xl"></div>
          
          {/* CTA Card */}
          <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-10 text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              <span className="bg-gradient-to-r from-white via-cyan-200 to-purple-300 bg-clip-text text-transparent">
                Ready to Transform Your Trading Operations with Institutional-Grade AI?
              </span>
            </h3>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <button
                onClick={scrollToContact}
                className="group px-6 py-3 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 rounded-xl font-bold shadow-lg hover:shadow-purple-500/30 transition-all duration-300 hover:scale-105 flex items-center gap-2 text-cyan-400"
              >
                <span>Schedule a Consultation for Your Institution</span>
                <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
            
            {/* Institutional Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-8 text-sm">
              <div className="flex items-center gap-2 text-gray-300">
                <SafeIcon icon={FiShield} className="text-cyan-400" />
                <span className="font-medium">Explore Tailored Enterprise Solutions & Pricing</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <SafeIcon icon={FiCheckCircle} className="text-cyan-400" />
                <span className="font-medium">Integrated Capital Preservation and Risk Controls</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <SafeIcon icon={FiCheckCircle} className="text-cyan-400" />
                <span className="font-medium">Pilot Your Custom Solution with No Lock-In</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section Component
const ContactSection = () => {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <section id="contact" className="relative py-32 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0d1a2d] border border-cyan-500/30 mb-8 shadow-lg shadow-cyan-500/10">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-500"></span>
            </span>
            <span className="text-sm font-medium bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Reach Out to Our Enterprise Team
            </span>
          </div>
          <h2 className="text-5xl md:text-6xl font-black mb-4">
            <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
              Contact Our Institutional Support Team
            </span>
          </h2>
          <div className="mb-6">
            <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Dedicated Support for Traders, Institutions & Enterprise Partners</span>
          </div>
          <p className="text-lg md:text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
            Our team is available Monday to Friday, 10am–5pm CST. We typically <span className="text-cyan-400 font-semibold">respond within 24 business hours.</span>
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="grid md:grid-cols-2 gap-12 bg-gray-800/50 backdrop-blur-sm border border-white/10 p-8 md:p-12 rounded-3xl"
        >
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-3xl font-bold mb-4 text-white">Institutional Contact Directory</h3>
              <p className="text-gray-400 mb-6">We'd love to hear from you. Send us a message and our team will respond as soon as possible.</p>
              <div className="mb-6 space-y-2">
                <p className="font-semibold text-lg bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                  Quantum AI Fintech Corporation SA
                </p>
                <p className="text-gray-400 flex items-start gap-2">
                  <img src={panamaFlag} alt="Panama Flag" className="w-6 h-4 object-cover rounded-sm mt-1 flex-shrink-0" />
                  <span>JJ Associates, Tower Financial Center, Office 28-F, Calle 50, Ciudad de Panamá, Panamá, 08501</span>
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex-shrink-0">
                  <SafeIcon icon={FiHeadphones} className="text-2xl text-cyan-400" />
                </div>
                <div>
                  <span className="text-white font-medium">support@qfintech.ai</span>
                  <p className="text-gray-500 text-sm">Client support, account assistance, platform help</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex-shrink-0">
                  <SafeIcon icon={FiDollarSign} className="text-2xl text-cyan-400" />
                </div>
                <div>
                  <span className="text-white font-medium">sales@qfintech.ai</span>
                  <p className="text-gray-500 text-sm">Institutional onboarding, licensing, enterprise plans</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex-shrink-0">
                  <SafeIcon icon={FiCamera} className="text-2xl text-cyan-400" />
                </div>
                <div>
                  <span className="text-white font-medium">media@qfintech.ai</span>
                  <p className="text-gray-500 text-sm">Press, analyst briefings, public relations</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex-shrink-0">
                  <SafeIcon icon={FiUsers} className="text-2xl text-cyan-400" />
                </div>
                <div>
                  <span className="text-white font-medium">partnerships@qfintech.ai</span>
                  <p className="text-gray-500 text-sm">Strategic partnerships, integrations, corporate collaborations</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="w-full">
            {submitted ? (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-700/50 p-8 rounded-2xl h-full flex flex-col items-center justify-center text-center"
              >
                <SafeIcon icon={FiCheckCircle} className="text-6xl text-green-400 mb-4"/>
                <h3 className="text-2xl font-bold mb-2 text-white">Message Sent!</h3>
                <p className="text-gray-300">Thank you for reaching out. We'll get back to you soon.</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">Name</label>
                  <input type="text" id="name" required className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-white" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                  <input type="email" id="email" required className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-white" />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1">Message</label>
                  <textarea id="message" rows="5" required className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-white"></textarea>
                </div>
                <motion.button 
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit" 
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-cyan-400 font-bold py-4 px-6 rounded-xl hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 transition-all shadow-lg hover:shadow-purple-500/30"
                >
                  <SafeIcon icon={FiSend} />
                  <span>Send Message</span>
                </motion.button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Mini CTA Component (réutilisable entre les sections)
const MiniCTA = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToPricing = () => {
    const element = document.getElementById('pricing');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          {/* Background Glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-cyan-600/20 to-pink-600/20 rounded-2xl blur-2xl"></div>
          
          {/* CTA Card */}
          <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-10 text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              <span className="bg-gradient-to-r from-white via-cyan-200 to-purple-300 bg-clip-text text-transparent">
                Ready to Transform Your Trading Operations with Institutional-Grade AI?
              </span>
            </h3>
            <p className="text-gray-400 mb-6 max-w-3xl mx-auto leading-relaxed">
              Deliver customized Quantum AI FinTech solutions tailored for financial institutions. Protect capital, optimize multi-asset portfolios, and automate decision-making with enterprise-grade AI risk management, quantum-inspired algorithms, and advanced trading technology.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <button
                onClick={scrollToContact}
                className="group px-6 py-3 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 rounded-xl font-bold shadow-lg hover:shadow-purple-500/30 transition-all duration-300 hover:scale-105 flex items-center gap-2 text-cyan-400"
              >
                <span>Schedule a Consultation for Your Institution</span>
                <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
            
            {/* Institutional Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-8 text-sm">
              <div className="flex items-center gap-2 text-gray-300">
                <SafeIcon icon={FiShield} className="text-cyan-400" />
                <span className="font-medium">Explore Tailored Enterprise Solutions & Pricing</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <SafeIcon icon={FiCheckCircle} className="text-cyan-400" />
                <span className="font-medium">Integrated Capital Preservation and Risk Controls</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <SafeIcon icon={FiCheckCircle} className="text-cyan-400" />
                <span className="font-medium">Pilot Your Custom Solution with No Lock-In</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const LandingPage = () => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.8]);
  const y = useTransform(scrollYProgress, [0, 1], [0, -100]);

  const springConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };
  const ySpring = useSpring(y, springConfig);

  const features = [
    {
      icon: FiShield,
      badge: 'RETAIL',
      badgeType: 'Hedge-Fund-Level Tools for Everyday Traders',
      title: 'SnapChart AI',
      subtitle: 'Key Features:',
      bullets: [
        'Structured market analysis for crypto, forex, and multi-asset markets',
        'AI-driven risk management and capital preservation guidance',
        'Real-time alerts and actionable trading insights',
        'Accountability tools to track performance and strategy adherence',
        'User-friendly dashboards designed for retail traders'
      ],
      footer: 'Access to hedge-fund-level analytics and risk systems previously only available to institutional traders. Improved decision-making with AI-backed insights. Protects capital while enhancing trading discipline.',
      delay: 0.1
    },
    {
      icon: GiSpartanHelmet,
      badge: 'ENTERPRISE',
      badgeType: 'Institutional-Grade Portfolio Optimization & Monitoring',
      title: 'TitanVue AI',
      subtitle: 'Key Features:',
      bullets: [
        'AI-powered predictive analytics and market insights',
        'Real-time portfolio performance tracking and risk exposure management',
        'Dynamic capital allocation across multiple asset classes',
        'Integration with institutional trading workflows and compliance systems',
        'Advanced reporting and actionable intelligence for decision-makers'
      ],
      footer: 'Institutional-grade portfolio oversight with predictive insights. Real-time risk management and capital optimization. Supports large-scale, multi-asset institutional operations.',
      delay: 0.2
    },
    {
      icon: FiCpu,
      badge: 'AI AGENTS',
      badgeType: 'Intelligent Agents Driving Automated Market Decisions',
      title: 'Plutus & Kronos',
      subtitle: 'Key Features:',
      bullets: [
        'Quantum-inspired decision intelligence embedded across trading systems',
        'Real-time adaptive strategy adjustment based on market conditions',
        'Automated risk monitoring and mitigation',
        'Continuous learning from market data to improve execution efficiency',
        'Supports copy trading, sniper trading, and multi-asset strategies'
      ],
      footer: 'AI-driven adaptive trading with real-time risk mitigation. Quantum-inspired precision for optimized decision-making. Enhances automation and efficiency across all trading strategies.',
      delay: 0.3
    },
    {
      icon: FiZap,
      badge: 'HFT BOT',
      badgeType: 'High-Frequency Trading, Optimized Across Chains',
      title: 'Crypto Arbitrage HFT Bot V2 – SOL & ETH',
      subtitle: 'Key Features:',
      bullets: [
        'Ultra-fast trade execution on Solana and Ethereum networks',
        'Multi-strategy arbitrage engine to exploit cross-exchange inefficiencies',
        'Quantum-inspired algorithms for risk-managed capital allocation',
        'Integration with DeFi and centralized exchange liquidity pools',
        'Automated monitoring of market conditions and opportunity identification'
      ],
      footer: 'Lightning-fast trade execution across multiple blockchains. Exploits arbitrage opportunities that human traders cannot. Institutional-grade capital preservation and risk control.',
      delay: 0.4
    },
    {
      icon: FiTarget,
      badge: 'SMART BOTS',
      badgeType: 'Customizable Bots for Every Market Opportunity',
      title: 'Smart Trading Bots – All Asset Classes',
      subtitle: 'Key Features:',
      bullets: [
        'Copy trading and sniper trading functionality for crypto, forex, and traditional assets',
        'Fully configurable strategies tailored to institutional or enterprise needs',
        'Automated risk controls and capital preservation features',
        'Integration with AI agents for real-time strategy optimization',
        'Cross-platform execution for maximum market coverage'
      ],
      footer: 'Flexible, AI-powered automation across all asset classes. Ensures risk-managed execution for professional-grade trading. Scales strategy deployment without human error or delay.',
      delay: 0.5
    },
    {
      icon: FiLayers,
      badge: 'CUSTOM',
      badgeType: 'Tailored AI and Quantum Solutions for Financial Institutions',
      title: 'Custom Enterprise Solutions',
      subtitle: 'Key Features:',
      bullets: [
        'End-to-end portfolio and trading infrastructure integration',
        'Multi-asset execution engines and advanced risk management tools',
        'Blockchain interoperability for cross-chain and multi-platform trading',
        'Plutus & Kronos AI agents embedded for adaptive decision-making',
        'Fully customizable to client-specific mandates, compliance, and operational requirements'
      ],
      footer: 'Fully bespoke enterprise solutions for institutional efficiency. Integrates AI, quantum-inspired trading, and risk systems into existing workflows. Delivers scalable, data-driven, and disciplined trading operations.',
      delay: 0.6
    }
  ];

  const stats = [
    { label: 'AI-Driven Predictive Algorithms', description: 'Uses machine learning and advanced statistical models to forecast market trends and price movements in crypto, forex, equities, and commodities.', icon: FiCpu },
    { label: 'High-Frequency Multi-Asset Trading', description: 'Executes trades in milliseconds across blockchains, forex markets, and traditional trading venues, capturing optimal opportunities efficiently.', icon: FiZap },
    { label: 'Real-Time Risk Management', description: 'Continuously monitors positions, volatility, and liquidity across all assets to minimize losses and preserve capital.', icon: FiShield },
    { label: 'Dynamic Capital Allocation', description: 'Automatically adjusts exposure across crypto, forex, and other markets based on risk, liquidity, and portfolio targets to safeguard investor funds.', icon: FiTrendingUp }
  ];

  const stats2 = [
    { label: 'AI Agents & Plutus/Kronos Integration', description: 'Intelligent agents powered by Plutus/Kronos optimize trade execution, strategy selection, and multi-asset decision-making across all platforms.', icon: FiTarget },
    { label: 'Data-Driven Analytics', description: 'Leverages real-time and historical market data across crypto, forex, equities, and commodities to drive strategic, performance-focused decisions.', icon: FiBarChart2 },
    { label: 'Institutional-Grade Intelligence', description: 'Incorporates quantitative research, deep learning models, and algorithmic strategies designed for professional-level trading across all asset classes.', icon: FiDatabase },
    { label: 'Customer-Centric & DeFi Integration', description: 'Delivers personalized strategies, reporting, and seamless execution through DeFi liquidity pools, forex liquidity providers, and traditional market connections.', icon: FiGlobe }
  ];

  const testimonials = [
    {
      name: 'Meridian Capital Partners',
      role: 'Multi-Strategy Hedge Fund',
      image: 'https://i.pravatar.cc/150?img=12',
      content: 'Quantum AI FinTech transformed our risk infrastructure. The AI-driven veto logic and real-time regime detection reduced our drawdowns by 35% while maintaining alpha generation across all asset classes.'
    },
    {
      name: 'Atlas Investment Bank',
      role: 'Global Investment Bank',
      image: 'https://i.pravatar.cc/150?img=45',
      content: 'We deployed Quantum AI across our proprietary trading desk. The institutional-grade discipline metrics and compliance documentation exceeded our regulatory requirements. A game-changer for enterprise risk management.'
    },
    {
      name: 'Nexus Digital Assets',
      role: 'Digital Asset Management Firm',
      image: 'https://i.pravatar.cc/150?img=33',
      content: 'The counterfactual engine and capital preservation analytics gave us unprecedented visibility into our crypto operations. Our institutional clients now have the transparency they demand.'
    },
    {
      name: 'Sterling Family Office',
      role: 'Multi-Family Office',
      image: 'https://i.pravatar.cc/150?img=52',
      content: 'Quantum AI integrated seamlessly into our wealth preservation framework. The TDI scoring provides our principals with institutional-grade accountability across all managed portfolios.'
    },
    {
      name: 'Apex Quantitative Research',
      role: 'Quantitative Trading Firm',
      image: 'https://i.pravatar.cc/150?img=47',
      content: 'The multi-timeframe regime classification rivals our internal models. Quantum AI catches structural shifts that even our quant systems miss. The NO-TRADE logic preserved seven figures last quarter.'
    },
    {
      name: 'Titan Prop Trading Group',
      role: 'Proprietary Trading Firm',
      image: 'https://i.pravatar.cc/150?img=57',
      content: 'We mandate Quantum AI for all funded traders. Behavioral analytics and discipline tracking reduced our capital blowout rate by 40%. This is the future of trader development at scale.'
    }
  ];

  return (
    <div ref={containerRef} className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white overflow-x-hidden">

      {/* Hero Section - What is Snap Chart AI */}
      <section id="what" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Quantum Planet Background - Right side */}
        <QuantumPlanetBackground className="right-[-10%] top-1/2 -translate-y-1/2 w-[800px] h-[800px] md:w-[1000px] md:h-[1000px] lg:w-[1200px] lg:h-[1200px] opacity-40" />
        
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-purple-500/20 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob"></div>
          <div className="absolute top-0 -right-4 w-96 h-96 bg-cyan-500/20 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-96 h-96 bg-pink-500/20 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob animation-delay-4000"></div>
          
          {/* Grid Overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f12_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f12_1px,transparent_1px)] bg-[size:64px_64px]"></div>
        </div>

        <motion.div 
          style={{ opacity, scale }}
          className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          {/* Premium Badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20 mb-8"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
            </span>
            <span className="text-sm font-medium bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Quantum-Powered Risk and Performance Intelligence
            </span>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-5xl md:text-7xl lg:text-8xl font-black mb-2 leading-tight"
          >
            <span className="bg-gradient-to-r from-white via-cyan-200 to-purple-300 bg-clip-text text-transparent">
              Quantum AI FinTech
            </span>
          </motion.h1>
          
          {/* Taglines */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mb-4"
          >
            <span className="text-xl md:text-2xl tracking-[0.15em] font-medium bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Enterprise-Grade AI Solutions for Financial Institutions and Trading Operations
            </span>
          </motion.div>
          
          

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="max-w-4xl mx-auto mb-12 space-y-6"
          >
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
              Designed to empower <span className="text-white font-semibold">banks</span>, <span className="text-white font-semibold">hedge funds</span>, <span className="text-white font-semibold">asset managers</span>, and <span className="text-white font-semibold">investment teams</span> with{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent font-medium">AI-driven risk management</span>,{' '}
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent font-medium">capital preservation</span>, and{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent font-medium">multi-asset execution intelligence</span>.
            </p>
            
            {/* Slogan */}
            <p className="text-xl md:text-2xl font-bold tracking-[0.2em] text-cyan-400 pt-4">
              Precision. Intelligence. Profit.
            </p>
          </motion.div>

          {/* First Row of Feature Boxes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mb-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                <div className="relative bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-cyan-500/50 transition-all duration-300 h-full">
                  <div className="flex justify-center mb-4">
                    <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
                      <SafeIcon icon={stat.icon} className="text-3xl text-cyan-400" />
                    </div>
                  </div>
                  <div className="text-lg font-bold text-white mb-2 text-center">{stat.label}</div>
                  <div className="text-xs text-gray-400 text-center leading-relaxed">{stat.description}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Second Row of Feature Boxes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto"
          >
            {stats2.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-600/20 to-blue-600/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                <div className="relative bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-cyan-500/50 transition-all duration-300 h-full">
                  <div className="flex justify-center mb-4">
                    <div className="p-3 rounded-xl bg-gradient-to-r from-cyan-600/20 to-blue-600/20 border border-cyan-500/30">
                      <SafeIcon icon={stat.icon} className="text-3xl text-cyan-400" />
                    </div>
                  </div>
                  <div className="text-lg font-bold text-white mb-2 text-center">{stat.label}</div>
                  <div className="text-xs text-gray-400 text-center leading-relaxed">{stat.description}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1 h-3 bg-white/50 rounded-full mt-2"
            />
          </div>
        </motion.div>
      </section>

      {/* Trusted By Logos Banner */}
      {/* Partners Section - 2026 Design */}
      <section id="partners" className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/[0.02] to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-gradient-to-r from-purple-500/5 via-cyan-500/5 to-purple-500/5 blur-3xl rounded-full" />
        
        <div className="max-w-7xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20 mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
              </span>
              <span className="text-sm font-medium bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Quantum AI Institutional Ecosystem
              </span>
            </div>
            <h3 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Partners & Key Suppliers
              </span>
            </h3>
            <div className="mb-6">
              <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Building the Future of Finance with Trusted Partners and Advanced Technology.</span>
            </div>
            <div className="text-lg md:text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              <p>The combination of cutting-edge infrastructure and proprietary trading technology enables precise, real-time decision-making across crypto, forex, and multi-asset markets, while maintaining robust risk management and capital preservation. Essentially, the platform is built on the strongest technological foundation to deliver both <span className="text-cyan-400 font-medium">speed and security</span> for professional and institutional users.</p>
            </div>
          </motion.div>

          {/* Categories */}
          <div className="space-y-12">
            {/* DEX Aggregators */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="relative"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-purple-500/30 to-transparent" />
                <span className="text-[10px] uppercase tracking-[0.25em] text-purple-400/80 font-semibold px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20">
                  DEX Aggregators
                </span>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-purple-500/30 to-transparent" />
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
                {['Jupiter', '1inch', 'Matcha', '0x', 'CoW Swap', 'OpenOcean', 'DexGuru', 'Rubic', '1Sol', 'Beetroot', 'Odos', 'KyberSwap', 'Velora', 'Orion', 'LI.FI', 'Rango', 'Socket', 'Birdeye', 'Swoop Exchange'].map((name, i) => (
                  <motion.div
                    key={name}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.03 }}
                    className="group relative px-4 py-2.5 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-purple-500/30 hover:bg-purple-500/[0.05] transition-all duration-300 cursor-default"
                  >
                    <span className="text-sm font-medium text-gray-400 group-hover:text-white transition-colors">{name}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Infrastructure & RPC */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
                <span className="text-[10px] uppercase tracking-[0.25em] text-cyan-400/80 font-semibold px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20">
                  Infrastructure & RPC
                </span>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
                {['Helius', 'QuickNode', 'Alchemy', 'Chainstack', 'Ankr', 'GetBlock', 'Blockdaemon', 'Triton One', 'Syndica', 'dRPC', 'Tatum', 'RockawayX', 'Vision Nodes', 'Assuga', 'Terra Switch', 'Latitude'].map((name, i) => (
                  <motion.div
                    key={name}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.03 }}
                    className="group relative px-4 py-2.5 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-cyan-500/30 hover:bg-cyan-500/[0.05] transition-all duration-300 cursor-default"
                  >
                    <span className="text-sm font-medium text-gray-400 group-hover:text-white transition-colors">{name}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Exchanges & DeFi */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="relative"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />
                <span className="text-[10px] uppercase tracking-[0.25em] text-emerald-400/80 font-semibold px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                  Exchanges & DeFi
                </span>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
                {['Bingx', 'Gate.io', 'Titan Exchange', 'Talos', 'Raydium', 'Meteora', 'Orca', 'Lifinity', 'Drift', 'Zeta Markets', 'Phoenix', 'Mango Markets', 'Jupiter Perps', 'Kamino', 'Marginfi', 'Solend', 'Jito', 'Marinade', 'Sanctum', 'Tensor', 'Magic Eden'].map((name, i) => (
                  <motion.div
                    key={name}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.03 }}
                    className="group relative px-4 py-2.5 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-emerald-500/30 hover:bg-emerald-500/[0.05] transition-all duration-300 cursor-default"
                  >
                    <span className="text-sm font-medium text-gray-400 group-hover:text-white transition-colors">{name}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Data & Analytics */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="relative"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
                <span className="text-[10px] uppercase tracking-[0.25em] text-amber-400/80 font-semibold px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20">
                  Data & Analytics
                </span>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
                {['TradingView', 'Bitquery', 'Solscan', 'CoinMarketCap', 'CoinGecko', 'Kaiko', 'CCData', 'CoinAPI', 'Amberdata', 'Coin Metrics', 'Messari', 'Glassnode', 'Santiment', 'CryptoQuant', 'IntoTheBlock', 'Nansen', 'Arkham', 'Token Terminal'].map((name, i) => (
                  <motion.div
                    key={name}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.03 }}
                    className="group relative px-4 py-2.5 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-amber-500/30 hover:bg-amber-500/[0.05] transition-all duration-300 cursor-default"
                  >
                    <span className="text-sm font-medium text-gray-400 group-hover:text-white transition-colors">{name}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* AI & Development */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="relative"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-rose-500/30 to-transparent" />
                <span className="text-[10px] uppercase tracking-[0.25em] text-rose-400/80 font-semibold px-4 py-1.5 rounded-full bg-rose-500/10 border border-rose-500/20">
                  AI & Development
                </span>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-rose-500/30 to-transparent" />
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
                {['Google Gemini', 'ChatGPT', 'Grok', 'Cursor', 'Greta', 'Lovable', 'Supabase', 'Heygen', 'Twilio'].map((name, i) => (
                  <motion.div
                    key={name}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.03 }}
                    className="group relative px-4 py-2.5 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-rose-500/30 hover:bg-rose-500/[0.05] transition-all duration-300 cursor-default"
                  >
                    <span className="text-sm font-medium text-gray-400 group-hover:text-white transition-colors">{name}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Strategic Partners */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="relative"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                <span className="text-[10px] uppercase tracking-[0.25em] text-white/80 font-semibold px-4 py-1.5 rounded-full bg-white/10 border border-white/20">
                  Strategic Partners
                </span>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
                {['Digital Trvst', 'Bloctech Investments'].map((name, i) => (
                  <motion.div
                    key={name}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.03 }}
                    className="group relative px-5 py-3 rounded-xl bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 hover:border-white/30 hover:from-white/[0.08] hover:to-white/[0.04] transition-all duration-300 cursor-default"
                  >
                    <span className="text-sm font-semibold text-gray-300 group-hover:text-white transition-colors">{name}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* Why Section */}
      <section id="why" className="relative py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0d1a2d] border border-cyan-500/30 mb-8 shadow-lg shadow-cyan-500/10">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-500"></span>
              </span>
              <span className="text-sm font-medium bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Built for Institutional Excellence
              </span>
            </div>
            <h3 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Why Quantum AI FinTech?
              </span>
            </h3>
            <div className="mb-6">
              <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Precision, Protection, and Performance for the Future of Finance.</span>
            </div>
            <div className="text-lg md:text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed space-y-4">
              <p>Quantum AI FinTech combines advanced AI, machine learning, and quantum-inspired algorithms to optimize trading, manage risk, and preserve capital across crypto, forex, and multi-asset markets.</p>
              <p>Its products and solutions provide institutional-grade analytics, data-driven decisions, and automated strategies that execute high-probability trades with efficiency and reliability.</p>
              <p>By leveraging cutting-edge technology, it empowers investors and institutions to navigate volatile markets confidently while <span className="text-cyan-400 font-medium">safeguarding capital and maximizing returns.</span></p>
            </div>
          </motion.div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'AI-Driven Decision Making',
                subtitle: '90% of top-performing fintech firms report improvement.',
                bullets: [
                  'AI and machine learning improve trading efficiency',
                  'Enhanced predictive accuracy',
                  'Automated strategy optimization',
                  'Real-time market analysis'
                ],
                footer: 'Smarter decisions powered by advanced AI algorithms.',
                icon: FiCpu,
                delay: 0.1
              },
              {
                title: 'Risk Management Optimization',
                subtitle: 'Advanced AI reduces portfolio drawdowns by 30–50%.',
                bullets: [
                  'Compared to traditional trading methods',
                  'Dynamic hedging strategies',
                  'Real-time risk monitoring'
                ],
                footer: 'Protect your capital with institutional-grade risk management.',
                icon: FiShield,
                delay: 0.2
              },
              {
                title: 'Quantum-Inspired Computation',
                subtitle: 'Exponentially faster market scenario simulation.',
                bullets: [
                  'Complex market scenarios at scale',
                  'High-frequency multi-asset arbitrage',
                  'Advanced pattern recognition',
                  'Faster than classical systems'
                ],
                footer: 'Quantum algorithms enable trading at unprecedented speed.',
                icon: FiZap,
                delay: 0.3
              },
              {
                title: 'Future of Capital Preservation',
                subtitle: 'By 2030, 70%+ institutional platforms will integrate AI.',
                bullets: [
                  'AI and quantum-inspired systems',
                  'Automated hedging solutions',
                  'Multi-asset portfolio optimization'
                ],
                footer: 'Leading the future of capital protection and growth.',
                icon: FiTrendingUp,
                delay: 0.4
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: feature.delay }}
                className="group relative"
              >
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
                
                {/* Card */}
                <div className="relative h-full bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-white/10 rounded-3xl p-8 hover:border-cyan-500/50 transition-all duration-500">
                  {/* Icon - styled like Powerful Features */}
                  <div className="flex justify-start mb-6">
                    <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 group-hover:scale-110 transition-transform duration-300">
                      <SafeIcon icon={feature.icon} className="text-3xl text-cyan-400" />
                    </div>
                  </div>
                  
                  {/* Title */}
                  <h4 className="text-2xl font-bold mb-2 text-white group-hover:text-cyan-400 transition-colors">
                    {feature.title}
                  </h4>
                  
                  {/* Subtitle */}
                  <p className="text-gray-300 mb-4 font-medium">
                    {feature.subtitle}
                  </p>
                  
                  {/* Bullets */}
                  <ul className="space-y-2 mb-5">
                    {feature.bullets.map((bullet, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                        <SafeIcon icon={FiCheck} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* Footer */}
                  <div className="pt-4 border-t border-white/5">
                    <p className="text-cyan-400 text-sm font-medium">
                      {feature.footer}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* How Section - Four Steps */}
      <section id="how" className="relative py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0d1a2d] border border-cyan-500/30 mb-8 shadow-lg shadow-cyan-500/10">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-500"></span>
              </span>
              <span className="text-sm font-medium bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Intelligent Capital, Multi-Asset Optimization, and Risk-Managed Performance
              </span>
            </div>
            <h3 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Leveraging Quantum AI FinTech
              </span>
            </h3>
            <div className="text-lg md:text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              <p>Quantum AI FinTech empowers investors and institutions to protect capital, optimize multi-asset portfolios, and execute trades with unmatched precision. By combining quantum-inspired algorithms, AI-driven risk management, and intelligent trading agents, our platform dynamically allocates capital, monitors risk in real time, and leverages liquidity across crypto, forex, and traditional markets. The result is an institutional-grade solution that delivers <span className="text-cyan-400 font-medium">data-driven insights, high-probability trade execution, and portfolio resilience</span> in any market environment.</p>
            </div>
          </motion.div>

          {/* Steps Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                step: '1',
                title: 'Protect & Preserve Capital',
                subtitle: 'Harness AI-driven risk management and quantum-inspired simulations to shield your portfolio from volatile market conditions across crypto, forex, and multi-asset markets.',
                delay: 0.1
              },
              {
                step: '2',
                title: 'Optimize Multi-Asset Performance',
                subtitle: 'Deploy dynamic capital allocation engines that adjust positions in real-time, leveraging liquidity across DEXs, CEXs, and DeFi pools for maximum efficiency.',
                delay: 0.2
              },
              {
                step: '3',
                title: 'Execute Smarter Trades',
                subtitle: 'Use intelligent trading agents and predictive algorithms to automatically select high-probability opportunities, reducing human error and emotional bias.',
                delay: 0.3
              },
              {
                step: '4',
                title: 'Access Institutional-Grade Insights',
                subtitle: 'Monitor portfolios with real-time analytics dashboards, track risk exposures, and make data-driven investment decisions with AI-powered intelligence.',
                delay: 0.4
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: item.delay }}
                className="group relative"
              >
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
                
                {/* Card */}
                <div className="relative h-full bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-white/10 rounded-3xl p-8 hover:border-cyan-500/50 transition-all duration-500">
                  {/* Step Badge */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 group-hover:scale-110 transition-transform duration-300">
                      <span className="text-2xl font-black bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">{item.step}</span>
                    </div>
                    <span className="text-xs font-bold text-purple-400 uppercase tracking-wider">Step {item.step}</span>
                  </div>
                  
                  {/* Title */}
                  <h4 className="text-2xl font-bold mb-4 text-white group-hover:text-cyan-400 transition-colors">
                    {item.title}
                  </h4>
                  
                  {/* Description */}
                  <p className="text-gray-400 leading-relaxed">
                    {item.subtitle}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* Features Section - Premium */}
      <section id="features" className="relative py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0d1a2d] border border-cyan-500/30 mb-8 shadow-lg shadow-cyan-500/10">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-500"></span>
              </span>
              <span className="text-sm font-medium bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Enterprise-Grade Tools for Capital Preservation
              </span>
            </div>
            <h3 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Powerful Products
              </span>
            </h3>
            <div className="mb-6">
              <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Enterprise-Grade Tools Driving Professional Trading and Risk Management</span>
            </div>
            <p className="text-lg md:text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              Our products deliver hedge-fund-level risk management, accountability systems, and structured market analysis — previously only accessible through multi-million-dollar infrastructure. Each product equips traders and institutions with professional-grade discipline, capital preservation, and AI-driven decision intelligence, <span className="text-cyan-400 font-semibold">giving you a competitive edge in crypto, forex, and multi-asset markets.</span>
            </p>
          </motion.div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: feature.delay }}
                className="group relative"
              >
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
                
                {/* Card */}
                <div className="relative h-full bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-white/10 rounded-3xl p-8 hover:border-cyan-500/50 transition-all duration-500">
                  {/* Feature Badge */}
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-2 py-1 text-[10px] font-bold tracking-wider bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border border-purple-500/40 rounded text-cyan-300 uppercase">
                      {feature.badge}
                    </span>
                    <span className="text-xs text-gray-400 font-medium">{feature.badgeType}</span>
                  </div>
                  
                  {/* Icon - styled like What section */}
                  <div className="flex justify-start mb-6">
                    <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 group-hover:scale-110 transition-transform duration-300">
                      <SafeIcon icon={feature.icon} className="text-3xl text-cyan-400" />
                    </div>
                  </div>
                  
                  {/* Title */}
                  <h4 className="text-xl font-bold mb-4 text-white group-hover:text-cyan-400 transition-colors">
                    {feature.title}
                  </h4>
                  
                  {/* Core Functions Header */}
                  <p className="text-xs font-bold text-purple-400 uppercase tracking-wider mb-2">Core Functions:</p>
                  <p className="text-gray-400 text-sm mb-3">
                    {feature.subtitle}
                  </p>
                  
                  {/* Bullets */}
                  <ul className="space-y-2 mb-5">
                    {feature.bullets.map((bullet, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                        <SafeIcon icon={FiCheck} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* Edge Footer */}
                  <div className="pt-4 border-t border-white/5">
                    <p className="text-xs font-bold text-purple-400 uppercase tracking-wider mb-1">Edge:</p>
                    <p className="text-cyan-400 text-sm font-medium">
                      {feature.footer}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* Pricing Section */}
      <PricingSection />


      {/* Testimonials Section */}
      <section id="testimonials" className="relative py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0d1a2d] border border-cyan-500/30 mb-8 shadow-lg shadow-cyan-500/10">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-500"></span>
              </span>
              <span className="text-sm font-medium bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Success Stories from Industry Leaders
              </span>
            </div>
            <h2 className="text-5xl md:text-6xl font-black mb-4">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Trusted by Financial Institutions and Professional Teams
              </span>
            </h2>
            <div className="mb-6">
              <span className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Real Results for Enterprise Trading Operations</span>
            </div>
            <p className="text-lg md:text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              Join leading banks, hedge funds, and trading firms leveraging <span className="text-cyan-400 font-semibold">Quantum AI FinTech's enterprise-grade AI solutions</span> to enhance decision-making, safeguard capital, and optimize multi-asset execution with institutional discipline.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-3xl p-8 hover:border-cyan-500/50 transition-all duration-300">
                  <div className="flex items-center gap-4 mb-6">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full border-2 border-cyan-500"
                    />
                    <div>
                      <div className="font-bold text-white">{testimonial.name}</div>
                      <div className="text-sm text-gray-400">{testimonial.role}</div>
                    </div>
                  </div>
                  <p className="text-gray-300 leading-relaxed italic">
                    "{testimonial.content}"
                  </p>
                  <div className="mt-6 flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <SafeIcon key={i} icon={FiCheckCircle} className="text-yellow-400" />
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* FAQ Section */}
      <section id="faq" className="relative py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0d1a2d] border border-cyan-500/30 mb-8 shadow-lg shadow-cyan-500/10">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-500"></span>
              </span>
              <span className="text-sm font-medium bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Get Answers to Your Questions
              </span>
            </div>
            <h2 className="text-5xl md:text-6xl font-black mb-6">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Frequently Asked Questions
              </span>
            </h2>
            <p className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Everything Enterprise Clients Need to Know
            </p>
            <p className="text-lg md:text-xl text-gray-400 max-w-4xl mx-auto mt-4 leading-relaxed">
              Comprehensive answers to common questions about institutional-grade <span className="text-cyan-400 font-semibold">AI trading solutions, risk management, and multi-asset execution</span> for professional operations.
            </p>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                question: "How accurate is Quantum AI FinTech's market analysis?",
                answer: "Our AI-driven predictive algorithms and quantum-inspired models provide institutional-grade insights with real-time adjustments to optimize performance across crypto, forex, and multi-asset markets."
              },
              {
                question: "Can I integrate Quantum AI solutions with my existing trading infrastructure?",
                answer: "Yes. Our enterprise solutions are fully customizable and designed to seamlessly integrate with your current workflows, execution engines, and compliance systems."
              },
              {
                question: "Which markets and asset classes are supported?",
                answer: "Our platforms cover crypto, forex, equities, and multi-asset portfolios, with high-frequency trading capabilities on Solana, Ethereum, and other supported blockchain networks."
              },
              {
                question: "Do you offer trial or demo options for enterprise clients?",
                answer: "Yes. Enterprise clients can request a pilot program or 30-day institutional trial to evaluate performance, risk management, and integration capabilities."
              },
              {
                question: "How secure is my trading and portfolio data?",
                answer: "Security is a top priority. All data is encrypted, and our systems comply with institutional-grade standards for confidentiality, data integrity, and operational security."
              },
              {
                question: "Can Quantum AI FinTech execute trades on my behalf?",
                answer: "Yes, through our smart trading bots and AI agents, trades can be executed automatically according to your risk parameters and strategic rules."
              },
              {
                question: "Is there support for ongoing customization and strategy optimization?",
                answer: "Absolutely. Our team provides continuous support, AI agent tuning, and enterprise-level strategy optimization to adapt to changing market conditions."
              },
              {
                question: "What is the subscription or licensing model for enterprise solutions?",
                answer: "We offer flexible enterprise licensing with options for annual or multi-year agreements, tailored to your organization's scale, trading volume, and desired AI features."
              }
            ].map((faq, index) => (
              <motion.details
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group bg-gray-800/30 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-purple-500/30 transition-all duration-300"
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <span className="text-lg font-semibold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent pr-4">{faq.question}</span>
                  <SafeIcon 
                    icon={FiChevronDown} 
                    className="text-cyan-400 transition-transform duration-300 group-open:rotate-180 flex-shrink-0" 
                  />
                </summary>
                <div className="px-6 pb-6 text-gray-300 leading-relaxed">
                  {faq.answer}
                </div>
              </motion.details>
            ))}
          </div>
        </div>
      </section>


      {/* Contact Section */}
      <ContactSection />

      {/* YouTube Video Modal - Rendered at root level to avoid z-index issues */}
      {showVideoModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 p-4"
          onClick={() => setShowVideoModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative w-full max-w-5xl aspect-video"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowVideoModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-cyan-400 transition-colors z-10"
            >
              <FiIcons.FiX className="text-3xl" />
            </button>
            <iframe
              src="https://www.youtube.com/embed/OsUH17WraYA?autoplay=1"
              title="Snap Chart AI Demo Video"
              className="w-full h-full rounded-2xl border border-white/20 shadow-2xl"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </motion.div>
        </div>
      )}

      <style jsx>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(20px, -50px) scale(1.1); }
          50% { transform: translate(-20px, 20px) scale(0.9); }
          75% { transform: translate(50px, 50px) scale(1.05); }
        }

        .animate-blob {
          animation: blob 7s infinite;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;